import {TranslateService} from '@ngx-translate/core';
import {
  afterNextRender, ChangeDetectorRef, Component, Inject, PLATFORM_ID,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ResponseNotificationService} from '../../core/services/response-notification.service';
import {
  ImageCropperComponent as NgxCropperComponent,
  ImageCroppedEvent,
  ImageTransform, LoadedImage
} from 'ngx-image-cropper';
import {SliderChangeEvent} from "primeng/slider";
import {ImageCropperState} from './image-crop-state';
import {ImagePaletteService} from '../background-panel/image-palette.service';

@Component({
  selector: 'app-image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrls: ['./image-cropper.component.scss'],
  host: {'[class.image-cropper-editor]': 'editorMode'},
})
export class ImageCropperComponent implements OnInit, OnDestroy, OnChanges {
  get permissions(): string[] {
    return this._permissions;
  }

  @Input() set permissions(value: string[]) {
    this._permissions = value;
  }

  get selectedImage(): File {
    return this._selectedImage;
  }

  set selectedImage(value: File) {
    this._selectedImage = value;
  }

  get parentHeight(): number {
    return this._parentHeight;
  }

  @Input() set parentHeight(value: number) {
    this._parentHeight = value;
  }
  get parentWidth(): number {
    return this._parentWidth;
  }

  @Input() set parentWidth(value: number) {
    this._parentWidth = value;
  }
  get spinnerState(): boolean {
    return this._spinnerState;
  }

  get styles(): object {
    return this._styles;
  }

  get imgContainerStyles(): object {
    return { ...{'background-image' : `url('${this.imagePath}')`}, ...this.imgStyles};
  }

  get removeDisabled() {
    return this.imagePath === this.defaultImage;
  }

  get imagePath(): string {
    return this.displayImage || this._imagePath || this.defaultImage;
  }

  @Input() set spinnerState(value: boolean) {
    this._spinnerState = value || false;
  }

  @Input() set imagePath(value: string) {
    this._imagePath = value;
    this.displayImage = null;
  }

  @Input() set styles(value: object) {
    this._styles = value;
  }

  @Input() imageFile?: File;
  @Input() editorMode = false;
  @Input() initialState?: ImageCropperState;
  @Input() maintainAspectRatio = false;
  @Input() imageFit: 'cover' | 'contain' = 'cover';
  @Input() showBackdrop = false;
  @Input() trimBackdropPadding = false;
  public unpaddedImageUrl: string | null = null;
  private paddingRequest = 0;
  private browserRendered = false;

  public get renderedImagePath(): string {
    return this.unpaddedImageUrl || this.imagePath;
  }

  @Output() readyChange = new EventEmitter<boolean>();
  @Output() sourceRatio = new EventEmitter<number>();
  @Output() exportFailed = new EventEmitter<void>();
  public ready = false;
  private destroyed = false;
  private stateRestored = false;

  @Input() cssAspectRatio?: string;
  @Input() externalControls = false;
  @Input() hoverZoom: boolean = true;
  @Input() pointerEvents: boolean = false;
  @Input() rounded: boolean = false;
  @Input() acceptLabel?: string;
  private _permissions: string[] = [];
  @Input() minCropImageWidth?: number;
  @Input() aspectRatio?: number;
  @Input() overflow?: boolean = false;
  @Input() resizeToWidth?: number;
  @Input() format: 'png' | 'jpeg' | 'bmp' | 'webp' | 'ico' = 'jpeg';
  @Input() imageQuality: number = 100;
  @Input() imgClass?: string;
  @Input() imgStyles?: object = {};
  @Input() toolboxEnabled?: boolean = true;
  @Input() toolboxLabel: string;
  @Input() public deleteEnabled?: boolean = true;
  @Input() public defaultImage: string = 'assets/img/car.webp';
  @Output() onImageRemoved: EventEmitter<void> = new EventEmitter<void>();
  @Output() onImageCropped: EventEmitter<File> = new EventEmitter<File>();
  @Output() onImageSelected: EventEmitter<File> = new EventEmitter<File>();
  @Output() onImageCanceled: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild(NgxCropperComponent) cComponent: NgxCropperComponent;
  public imageChangedEvent: any = null;
  private _imagePath: string;
  private _styles: object;
  private displayImage: any;
  private _selectedImage: File;
  public croppedImage: File;
  private imgDisabled: boolean = true;
  private _spinnerState: boolean;
  private _parentWidth: number;
  private _parentHeight: number;
  public transform: ImageTransform = {scale: 1}
  public scale: number = 1;
  private sourceImageWidth?: number;

  constructor(
    private rns: ResponseNotificationService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly translate: TranslateService,
    private readonly palettes: ImagePaletteService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    afterNextRender(() => {
      this.browserRendered = true;
      this.updateBackdropPadding();
    });
  }

  ngOnInit() {
    this.minCropImageWidth = this.minCropImageWidth ?? 900;
    this.spinnerState = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (['imagePath', 'showBackdrop', 'trimBackdropPadding', 'imageFit', 'editorMode', 'rounded'].some(key => changes[key])) {
      this.updateBackdropPadding();
    }
    if (changes['imageFile'] && isPlatformBrowser(this.platformId)) {
      this.selectedImage = this.imageFile || null;
      this.imageChangedEvent = null;
      this.transform = this.editorMode && this.initialState ? {...this.initialState.transform} : {scale: 1};
      this.scale = this.transform.scale || 1;
      this.stateRestored = false;
      this.setReady(false);
    }
  }

  private updateBackdropPadding(): void {
    const request = ++this.paddingRequest;
    this.unpaddedImageUrl = null;
    if (!this.browserRendered || !this.showBackdrop || !this.trimBackdropPadding
      || this.editorMode) return;
    const source = this.imagePath;
    if (!source) return;
    this.palettes.getPalette(source).then(palette => {
      if (this.destroyed || request !== this.paddingRequest || source !== this.imagePath || this.selectedImage) return;
      this.unpaddedImageUrl = palette?.contentUrl || null;
      this.cdr.markForCheck();
    });
  }

  imageLoaded(image: LoadedImage): void {
    this.sourceImageWidth = image.transformed.size.width;
    this.sourceRatio.emit(image.transformed.size.width / image.transformed.size.height);
  }

  cropperReady(): void {
    if (this.editorMode && this.initialState && !this.stateRestored) {
      const {position, dimensions, transform} = this.initialState;
      const size = this.cComponent.maxSize;
      if (dimensions.width > 0 && dimensions.height > 0 && size.width > 0 && size.height > 0) {
        const scaleX = size.width / dimensions.width;
        const scaleY = size.height / dimensions.height;
        // ngx-image-cropper resets the frame while decoding. Restore it only after
        // the source has measurable dimensions, using the current viewport size.
        Object.assign(this.cComponent.cropper, {
          x1: position.x1 * scaleX, x2: position.x2 * scaleX,
          y1: position.y1 * scaleY, y2: position.y2 * scaleY,
        });
        this.transform = {...transform};
        if (transform.translateUnit === 'px') {
          this.transform.translateH = (transform.translateH || 0) * scaleX;
          this.transform.translateV = (transform.translateV || 0) * scaleY;
        }
        this.scale = this.transform.scale || 1;
        this.stateRestored = true;
      }
    }
    this.setReady(true);
  }

  captureState(): ImageCropperState | undefined {
    if (!this.editorMode || !this.ready || !isPlatformBrowser(this.platformId) || !this.cComponent) { return; }
    const {cropper, maxSize, transform} = this.cComponent;
    if (!maxSize.width || !maxSize.height) { return; }
    return {position: {...cropper}, dimensions: {...maxSize}, transform: {...transform}};
  }

  private setReady(ready: boolean): void {
    this.ready = ready;
    this.readyChange.emit(ready);
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.ready = false;
  }

  fileChangeEvent(event: any): void {
    if (isPlatformBrowser(this.platformId) && event.target.files && event.target.files[0]) {
      this.setReady(false);
      this.selectedImage = (event.target.files[0] as File);
      this.imageChangedEvent = event;
      this.scale = 1;
      this.transform = {scale: 1};

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.sourceImageWidth = undefined;
        this.displayImage = e.target.result;
        this.captureSourceImageWidth(e.target.result);
      };
      reader.readAsDataURL(this.selectedImage);
      this.onImageSelected.emit(this.selectedImage);
    }
  }

  public blobToFile(blob: Blob) {
    const tn = this.selectedImage.name.split('.');
    tn.pop();
    const blobType = blob.type || this.selectedImage.type;
    const extension = blobType.split('/')[1] || 'jpg';
    const name = tn.join('.') + '.' + extension;
    return new File([blob], name, {type: blobType, lastModified: Date.now()});
  }

  async onImgCropped(event: ImageCroppedEvent) {
    if (event.objectUrl && isPlatformBrowser(this.platformId)) {
      URL.revokeObjectURL(event.objectUrl);
    }
    if (this.destroyed || !this.selectedImage) { return; }
    if (!event.blob) {
      this.exportFailed.emit();
      return;
    }
    this.croppedImage = this.blobToFile(event.blob);

    if (typeof this.displayImage === 'string') {
      this.imgDisabled = false;
    }
    this.emitCroppedImage();
  }

  cancelImgAdd(): void {
    this.setReady(false);
    this.displayImage = null;
    this.selectedImage = null;
    this.imageChangedEvent = null;
    this.sourceImageWidth = undefined;
    this.scale = 1;
    this.transform = {scale: 1};
    this.onImageCanceled.emit();
  }

  triggerFileUploadClick(): void {
    this.fileInput.nativeElement.click();
  }

  triggerRemoveClick(): void {
    this.onImageRemoved.emit();
  }

  onFail(error) {
    this.setReady(false);
    this.rns.emitError(this.translate.instant('shared.image_cropper.load_error'));
    this.selectedImage = null;
    this.imageChangedEvent = null;
    this.displayImage = null;
    this.sourceImageWidth = undefined;
    this.scale = 1;
    this.transform = {scale: 1};
    this.onImageCanceled.emit();
  }

  handleChange(e: SliderChangeEvent) {
    this.transform = {...this.transform, scale: this.scale};
  }

  emitCroppedImage(): void {
    this.onImageCropped.emit(this.croppedImage);
    this.setReady(false);
    this.selectedImage = null;
    this.imageChangedEvent = null;
    this.displayImage = null;
    this.sourceImageWidth = undefined;
    this.scale = 1;
    this.transform = {scale: 1};
  }

  async cropImage(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.ready || !this.cComponent) { return; }
    try {
      const result = await this.cComponent.crop('blob');
      if (!result && !this.destroyed) { this.exportFailed.emit(); }
    } catch {
      if (!this.destroyed) { this.exportFailed.emit(); }
    }
  }

  get effectiveResizeToWidth(): number | undefined {
    if (!this.resizeToWidth || !this.sourceImageWidth) {
      return undefined;
    }

    return this.sourceImageWidth > this.resizeToWidth ? this.resizeToWidth : undefined;
  }

  private captureSourceImageWidth(source: string): void {
    const image = new Image();
    image.onload = () => {
      this.sourceImageWidth = image.naturalWidth || undefined;
    };
    image.onerror = () => {
      this.sourceImageWidth = undefined;
    };
    image.src = source;
  }
}
